import React, { useState, useEffect } from 'react';
import { Edit3, Save, Plus, Trash2, ArrowLeft, Check, X } from 'lucide-react';
import supabase from '../../supabase';
import './EmpSalaryEdit.css';

// Helper function to get days in current month
const getDaysInCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const EmpSalaryEdit = ({ employeeId, paySlipId, onBack, onSave }) => {
  // Remove useParams, useNavigate, useLocation - get employeeId from props
  
  // Debug logging
  console.log('EmpSalaryEdit - received employeeId:', employeeId);
  console.log('EmpSalaryEdit - received paySlipId:', paySlipId);
  
  const [employee, setEmployee] = useState(null);
  const [company, setCompany] = useState(null);
  const [paySlip, setPaySlip] = useState({
    std_days: getDaysInCurrentMonth(), // Updated to use current month days
    working_days: getDaysInCurrentMonth(), // Updated to use current month days, equal to std_days
    no_of_leaves: 0,
    transaction_date: new Date().toISOString().split('T')[0],
    earnings: {},
    deductions: {},
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [editingSections, setEditingSections] = useState({
    workingDays: false,
    earnings: false,
    deductions: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [newEarningName, setNewEarningName] = useState('');
  const [newDeductionName, setNewDeductionName] = useState('');
  const [showAddEarning, setShowAddEarning] = useState(false);
  const [showAddDeduction, setShowAddDeduction] = useState(false);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    if (employeeId) {
      fetchData();
    }
  }, [employeeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch employee data with all fields
      const { data: empData, error: empError } = await supabase
        .from('employees')
        .select(`
          id,
          user_id,
          name,
          emp_role,
          joining_date,
          salary,
          emp_bank_name,
          account_no,
          ifsc_code,
          pan_card,
          location,
          pf_no,
          working_days,
          days_paid,
          leave_days,
          image,
          custom_fields,
          emp_id,
          branch_name,
          earnings,
          deductions
        `)
        .eq('id', employeeId)
        .single();

      if (empError) {
        console.error('Error fetching employee:', empError);
        throw empError;
      }

      setEmployee(empData);

      // Fetch company data from users table
      const { data: companyData, error: companyError } = await supabase
        .from('users')
        .select(`
          company_name,
          address,
          gst_number,
          logo_url,
          subscription_id
        `)
        .eq('id', empData.user_id)
        .single();

      if (companyError) {
        console.error('Error fetching company:', companyError);
        throw companyError;
      }

      setCompany(companyData);

      // Check if we're editing an existing payslip or creating new one
      if (paySlipId && paySlipId !== 'new') {
        // Load existing payslip data by ID
        const { data: existingPaySlip, error: paySlipError } = await supabase
          .from('pay_slips')
          .select(`
            id,
            employee_id,
            user_id,
            earnings,
            deductions,
            month,
            year,
            total_amount,
            transaction_date,
            std_days,
            working_days,
            no_of_leaves
          `)
          .eq('id', paySlipId)
          .single();

        if (paySlipError) {
          console.error('Error fetching payslip:', paySlipError);
        } else if (existingPaySlip) {
          setPaySlip(existingPaySlip);
        }
      } else {
        // Check if there's an existing payslip for this employee for current month/year
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();
        
        const { data: existingPaySlip, error: paySlipError } = await supabase
          .from('pay_slips')
          .select(`
            id,
            employee_id,
            user_id,
            earnings,
            deductions,
            month,
            year,
            total_amount,
            transaction_date,
            std_days,
            working_days,
            no_of_leaves
          `)
          .eq('employee_id', employeeId)
          .eq('month', currentMonth)
          .eq('year', currentYear)
          .single();

        if (paySlipError && paySlipError.code !== 'PGRST116') { // PGRST116 is "not found" error
          console.error('Error fetching payslip:', paySlipError);
        }

        if (existingPaySlip && !paySlipError) {
          // Load existing payslip data
          setPaySlip(existingPaySlip);
        } else {
          // Initialize new payslip with employee default values
          const defaultEarnings = empData.earnings || {
            'Basic Salary': Math.round(empData.salary * 0.5) || 0,
            'House Rent Allowance': Math.round(empData.salary * 0.2) || 0,
            'Travel Allowance': Math.round(empData.salary * 0.1) || 0,
            'Performance Pay': Math.round(empData.salary * 0.2) || 0
          };

          const defaultDeductions = empData.deductions || {
            'Professional Tax': 200
          };

          const daysInCurrentMonth = getDaysInCurrentMonth();

          setPaySlip(prev => ({
            ...prev,
            earnings: defaultEarnings,
            deductions: defaultDeductions,
            working_days: daysInCurrentMonth, // Updated to use current month days
            no_of_leaves: empData.leave_days || 0,
            std_days: daysInCurrentMonth // Updated to use current month days
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setSaveMessage('Error loading data. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Use the onBack prop function
    if (onBack) {
      onBack();
    }
  };

  const toggleEdit = (section) => {
    setEditingSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
    
    // Reset add forms when exiting edit mode
    if (editingSections[section]) {
      setShowAddEarning(false);
      setShowAddDeduction(false);
      setNewEarningName('');
      setNewDeductionName('');
    }
  };

  const updatePaySlipField = (field, value) => {
    setPaySlip(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateEarningDeduction = (type, key, value, isDelete = false) => {
    setPaySlip(prev => {
      const updated = { ...prev[type] };
      if (isDelete) {
        delete updated[key];
      } else {
        updated[key] = parseFloat(value) || 0;
      }
      return {
        ...prev,
        [type]: updated
      };
    });
  };

  const addEarningDeduction = (type) => {
    if (type === 'earnings') {
      setShowAddEarning(true);
    } else {
      setShowAddDeduction(true);
    }
  };

  const confirmAddEarning = () => {
    if (newEarningName.trim()) {
      updateEarningDeduction('earnings', newEarningName.trim(), 0);
      setNewEarningName('');
      setShowAddEarning(false);
    }
  };

  const confirmAddDeduction = () => {
    if (newDeductionName.trim()) {
      updateEarningDeduction('deductions', newDeductionName.trim(), 0);
      setNewDeductionName('');
      setShowAddDeduction(false);
    }
  };

  const cancelAddEarning = () => {
    setNewEarningName('');
    setShowAddEarning(false);
  };

  const cancelAddDeduction = () => {
    setNewDeductionName('');
    setShowAddDeduction(false);
  };

  const calculateTotal = (items) => {
    return Object.values(items || {}).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
  };

  const calculateNetPay = () => {
    const totalEarnings = calculateTotal(paySlip.earnings);
    const totalDeductions = calculateTotal(paySlip.deductions);
    return totalEarnings - totalDeductions;
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convert = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };

    if (num === 0) return 'Zero Rupees Only';
    return convert(Math.floor(Math.abs(num))) + ' Rupees Only';
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const totalAmount = calculateNetPay();
      
      const paySlipData = {
        employee_id: employeeId,
        user_id: employee.user_id,
        earnings: paySlip.earnings,
        deductions: paySlip.deductions,
        month: paySlip.month,
        year: paySlip.year,
        total_amount: totalAmount,
        transaction_date: paySlip.transaction_date,
        std_days: paySlip.std_days,
        working_days: paySlip.working_days,
        no_of_leaves: paySlip.no_of_leaves
      };

      let savedPaySlip = null;

      // Check if we're updating an existing payslip
      if (paySlip.id) {
        // Update existing payslip
        const { data, error } = await supabase
          .from('pay_slips')
          .update(paySlipData)
          .eq('id', paySlip.id)
          .select()
          .single();

        if (error) throw error;
        savedPaySlip = data;
      } else {
        // Check if payslip already exists for this month/year
        const { data: existingPaySlip } = await supabase
          .from('pay_slips')
          .select('id')
          .eq('employee_id', employeeId)
          .eq('month', paySlip.month)
          .eq('year', paySlip.year)
          .single();

        if (existingPaySlip) {
          // Update existing payslip
          const { data, error } = await supabase
            .from('pay_slips')
            .update(paySlipData)
            .eq('id', existingPaySlip.id)
            .select()
            .single();

          if (error) throw error;
          savedPaySlip = data;
        } else {
          // Create new payslip
          const { data, error } = await supabase
            .from('pay_slips')
            .insert([paySlipData])
            .select()
            .single();

          if (error) throw error;
          savedPaySlip = data;
        }
      }

      setSaveMessage('Salary slip saved successfully!');
      
      // Call onSave callback if provided, passing the saved payslip data
      setTimeout(() => {
        setSaveMessage('');
        if (onSave) {
          onSave(savedPaySlip);
        } else {
          handleBack();
        }
      }, 2000);

    } catch (error) {
      console.error('Error saving pay slip:', error);
      setSaveMessage('Error saving salary slip. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Early return for missing employeeId
  if (!employeeId) {
    return (
      <div className="empsalaryedit-container">
        <div className="empsalaryedit-loading">Employee ID not provided</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="empsalaryedit-container">
        <div className="empsalaryedit-loading">Loading...</div>
      </div>
    );
  }

  if (!employee || !company) {
    return (
      <div className="empsalaryedit-container">
        <div className="empsalaryedit-loading">Employee or Company data not found.</div>
      </div>
    );
  }

  return (
    <div className="empsalaryedit-container">
      
      <div className="empsalaryedit-slip">
        {/* Header Section */}
        <div className="empsalaryedit-slip-header">
          <div className="empsalaryedit-company-section">
            {company.logo_url && (
              <img 
                src={company.logo_url} 
                alt="Company Logo" 
                className="empsalaryedit-logo"
              />
            )}
            <h1 className="empsalaryedit-company-name">
              {company.company_name || 'Company Name'}
            </h1>
          </div>
          <div className="empsalaryedit-slip-title">
            <h2>PAY SLIP</h2>
            <p className="empsalaryedit-month-year">{monthNames[paySlip.month - 1]} {paySlip.year}</p>
          </div>
        </div>

        {/* Employee Information */}
        <div className="empsalaryedit-employee-section">
          <div className="empsalaryedit-employee-grid">
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Employee Name:</span>
              <span className="empsalaryedit-value">{employee.name || 'N/A'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Employee ID:</span>
              <span className="empsalaryedit-value">{employee.emp_id || 'N/A'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Designation:</span>
              <span className="empsalaryedit-value">{employee.emp_role || 'N/A'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Date of Joining:</span>
              <span className="empsalaryedit-value">
                {employee.joining_date ? new Date(employee.joining_date).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: '2-digit', 
                  year: 'numeric'
                }) : 'N/A'}
              </span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Bank Name:</span>
              <span className="empsalaryedit-value">{employee.emp_bank_name || 'State Bank of India'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Account No.:</span>
              <span className="empsalaryedit-value">{employee.account_no || 'N/A'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">IFSC Code:</span>
              <span className="empsalaryedit-value">{employee.ifsc_code || 'SBIN0020007'}</span>
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">PAN Number:</span>
              <span className="empsalaryedit-value">{employee.pan_card || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Days Information */}
        <div className="empsalaryedit-days-section">
          <div className="empsalaryedit-section-header">
            <h3>Days Information</h3>
            <button 
              className="empsalaryedit-edit-btn"
              onClick={() => toggleEdit('workingDays')}
            >
              <Edit3 size={14} />
              {editingSections.workingDays ? 'Done' : 'Edit'}
            </button>
          </div>
          <div className="empsalaryedit-days-grid">
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">STD days:</span>
              {editingSections.workingDays ? (
                <input 
                  type="number" 
                  value={paySlip.std_days}
                  onChange={(e) => {
                    const newStdDays = parseInt(e.target.value) || 0;
                    setPaySlip(prev => ({
                      ...prev,
                      std_days: newStdDays,
                      working_days: newStdDays // Keep working_days equal to std_days
                    }));
                  }}
                  className="empsalaryedit-input"
                  min="0"
                />
              ) : (
                <span className="empsalaryedit-value">{paySlip.std_days}</span>
              )}
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Paid Days:</span>
              {editingSections.workingDays ? (
                <input 
                  type="number" 
                  value={paySlip.working_days}
                  onChange={(e) => updatePaySlipField('working_days', parseInt(e.target.value) || 0)}
                  className="empsalaryedit-input"
                  min="0"
                />
              ) : (
                <span className="empsalaryedit-value">{paySlip.working_days}</span>
              )}
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">Leaves:</span>
              {editingSections.workingDays ? (
                <input 
                  type="number" 
                  value={paySlip.no_of_leaves}
                  onChange={(e) => updatePaySlipField('no_of_leaves', parseInt(e.target.value) || 0)}
                  className="empsalaryedit-input"
                  min="0"
                />
              ) : (
                <span className="empsalaryedit-value">{paySlip.no_of_leaves}</span>
              )}
            </div>
            <div className="empsalaryedit-info-item">
              <span className="empsalaryedit-label">LOP Days:</span>
              <span className="empsalaryedit-value">0</span>
            </div>
            <div className="empsalaryedit-info-item full-width">
              <span className="empsalaryedit-label">Transaction Date:</span>
              {editingSections.workingDays ? (
                <input 
                  type="date" 
                  value={paySlip.transaction_date}
                  onChange={(e) => updatePaySlipField('transaction_date', e.target.value)}
                  className="empsalaryedit-input"
                />
              ) : (
                <span className="empsalaryedit-value">
                  {new Date(paySlip.transaction_date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: '2-digit', 
                    year: 'numeric'
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Earnings and Deductions */}
        <div className="empsalaryedit-earnings-deductions">
          {/* Earnings */}
          <div className="empsalaryedit-earnings-section">
            <div className="empsalaryedit-section-header">
              <h3>Earnings</h3>
              <div className="empsalaryedit-section-actions">
                {editingSections.earnings && (
                  <button 
                    className="empsalaryedit-add-btn"
                    onClick={() => addEarningDeduction('earnings')}
                    title="Add new earning"
                  >
                    <Plus size={12} />
                  </button>
                )}
                <button 
                  className="empsalaryedit-edit-btn"
                  onClick={() => toggleEdit('earnings')}
                >
                  <Edit3 size={14} />
                  {editingSections.earnings ? 'Done' : 'Edit'}
                </button>
              </div>
            </div>
            <div className="empsalaryedit-items-list">
              {showAddEarning && (
                <div className="empsalaryedit-add-form">
                  <input
                    type="text"
                    value={newEarningName}
                    onChange={(e) => setNewEarningName(e.target.value)}
                    placeholder="Enter earning name"
                    className="empsalaryedit-name-input"
                    onKeyPress={(e) => e.key === 'Enter' && confirmAddEarning()}
                  />
                  <div className="empsalaryedit-form-actions">
                    <button className="empsalaryedit-confirm-btn" onClick={confirmAddEarning}>
                      <Check size={12} />
                    </button>
                    <button className="empsalaryedit-cancel-btn" onClick={cancelAddEarning}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
              
              {Object.entries(paySlip.earnings || {}).map(([key, value]) => (
                <div key={key} className="empsalaryedit-item-row">
                  <span className="empsalaryedit-item-name">{key}</span>
                  {editingSections.earnings ? (
                    <div className="empsalaryedit-item-controls">
                      <input 
                        type="number" 
                        value={value}
                        onChange={(e) => updateEarningDeduction('earnings', key, e.target.value)}
                        className="empsalaryedit-amount-input"
                        min="0"
                        step="0.01"
                      />
                      <button 
                        className="empsalaryedit-delete-btn"
                        onClick={() => updateEarningDeduction('earnings', key, null, true)}
                        title="Delete this earning"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="empsalaryedit-item-amount">₹{(parseFloat(value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  )}
                </div>
              ))}
              <div className="empsalaryedit-total-row">
                <span className="empsalaryedit-total-label">Total Earnings</span>
                <span className="empsalaryedit-total-amount">₹{calculateTotal(paySlip.earnings).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Deductions */}
          <div className="empsalaryedit-deductions-section">
            <div className="empsalaryedit-section-header">
              <h3>Deductions</h3>
              <div className="empsalaryedit-section-actions">
                {editingSections.deductions && (
                  <button 
                    className="empsalaryedit-add-btn"
                    onClick={() => addEarningDeduction('deductions')}
                    title="Add new deduction"
                  >
                    <Plus size={12} />
                  </button>
                )}
                <button 
                  className="empsalaryedit-edit-btn"
                  onClick={() => toggleEdit('deductions')}
                >
                  <Edit3 size={14} />
                  {editingSections.deductions ? 'Done' : 'Edit'}
                </button>
              </div>
            </div>
            <div className="empsalaryedit-items-list">
              {showAddDeduction && (
                <div className="empsalaryedit-add-form">
                  <input
                    type="text"
                    value={newDeductionName}
                    onChange={(e) => setNewDeductionName(e.target.value)}
                    placeholder="Enter deduction name"
                    className="empsalaryedit-name-input"
                    onKeyPress={(e) => e.key === 'Enter' && confirmAddDeduction()}
                  />
                  <div className="empsalaryedit-form-actions">
                    <button className="empsalaryedit-confirm-btn" onClick={confirmAddDeduction}>
                      <Check size={12} />
                    </button>
                    <button className="empsalaryedit-cancel-btn" onClick={cancelAddDeduction}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
              
              {Object.entries(paySlip.deductions || {}).map(([key, value]) => (
                <div key={key} className="empsalaryedit-item-row">
                  <span className="empsalaryedit-item-name">{key}</span>
                  {editingSections.deductions ? (
                    <div className="empsalaryedit-item-controls">
                      <input 
                        type="number" 
                        value={value}
                        onChange={(e) => updateEarningDeduction('deductions', key, e.target.value)}
                        className="empsalaryedit-amount-input"
                        min="0"
                        step="0.01"
                      />
                      <button 
                        className="empsalaryedit-delete-btn"
                        onClick={() => updateEarningDeduction('deductions', key, null, true)}
                        title="Delete this deduction"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="empsalaryedit-item-amount">₹{(parseFloat(value) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  )}
                </div>
              ))}
              <div className="empsalaryedit-total-row">
                <span className="empsalaryedit-total-label">Total Deductions</span>
                <span className="empsalaryedit-total-amount">₹{calculateTotal(paySlip.deductions).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Net Pay Section */}
        <div className="empsalaryedit-netpay-section">
          <div className="empsalaryedit-netpay-row">
            <span className="empsalaryedit-netpay-label">Net Pay:</span>
            <span className="empsalaryedit-netpay-amount">₹{calculateNetPay().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="empsalaryedit-netpay-words">
            ({numberToWords(calculateNetPay())})
          </div>
        </div>

        {/* Footer Note */}
        <div className="empsalaryedit-footer">
          <p>This is a computer-generated salary slip and does not require a signature.</p>
        </div>
      </div>

      <div className="empsalaryedit-header">
        {saveMessage && (
          <div className={`empsalaryedit-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}
        
        <div className="empsalaryedit-action-buttons">
          <button 
            className="empsalaryedit-back-btn" 
            onClick={handleBack}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          
          <button 
            className="empsalaryedit-save-btn" 
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Slip'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmpSalaryEdit;